import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Button,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAt, IconCheck, IconX } from "@tabler/icons-react";
import { useAuth } from "../lib/authContext";
import {
  isUsernameAvailable,
  UsernameTakenError,
  validateUsername,
} from "../lib/usernames";

type AvailabilityState =
  | { status: "idle" }
  | { status: "invalid"; error: string }
  | { status: "checking" }
  | { status: "taken" }
  | { status: "available" };

export function UsernameClaimModal() {
  const { needsUsername, claimUsername, signOutUser } = useAuth();
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityState>({
    status: "idle",
  });
  // Debounce the network check so we don't pelt Firestore on every keystroke.
  const checkSeqRef = useRef(0);

  useEffect(() => {
    if (!needsUsername) {
      setValue("");
      setAvailability({ status: "idle" });
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [needsUsername]);

  useEffect(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      setAvailability({ status: "idle" });
      return;
    }
    const v = validateUsername(trimmed);
    if (!v.ok) {
      setAvailability({ status: "invalid", error: v.error });
      return;
    }
    setAvailability({ status: "checking" });
    const seq = ++checkSeqRef.current;
    const handle = window.setTimeout(() => {
      isUsernameAvailable(trimmed)
        .then((available) => {
          if (seq !== checkSeqRef.current) return;
          setAvailability({ status: available ? "available" : "taken" });
        })
        .catch(() => {
          if (seq !== checkSeqRef.current) return;
          // Fall back to letting the user submit; the claim transaction is
          // the real source of truth on availability.
          setAvailability({ status: "idle" });
        });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [value]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const v = validateUsername(value);
    if (!v.ok) {
      setSubmitError(v.error);
      return;
    }
    setSubmitting(true);
    try {
      await claimUsername(value.trim());
      // Success: AuthProvider's onSnapshot will flip needsUsername to false
      // and this modal will unmount itself.
    } catch (err) {
      if (err instanceof UsernameTakenError) {
        setSubmitError("That username was just taken. Try another.");
        setAvailability({ status: "taken" });
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Couldn't claim that handle. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const helper = renderHelper(availability);
  const canSubmit =
    !submitting &&
    (availability.status === "available" || availability.status === "idle");

  return (
    <Modal
      opened={needsUsername}
      onClose={() => {
        // Forced flow - closing without a username is a no-op. If they want
        // out, they can sign out.
      }}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title="Pick a username"
      centered
      size="sm"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            This is the handle that'll show up on your profile and anywhere
            you share something from a nifusion.dev app. You can change it
            later, but only once every 30 days.
          </Text>
          <TextInput
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            placeholder="nifusion"
            leftSection={<IconAt size={14} />}
            autoFocus
            data-autofocus
            maxLength={20}
            error={submitError ?? undefined}
          />
          {helper}
          <Button
            type="submit"
            loading={submitting}
            disabled={!canSubmit}
            fullWidth
          >
            Claim @{value.trim() || "username"}
          </Button>
          <Button
            type="button"
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => signOutUser()}
          >
            Sign out instead
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}

function renderHelper(state: AvailabilityState) {
  switch (state.status) {
    case "idle":
      return (
        <Text size="xs" c="dimmed">
          3-20 characters. Letters, numbers, underscores. Starts with a letter.
        </Text>
      );
    case "invalid":
      return (
        <Text size="xs" c="red">
          {state.error}
        </Text>
      );
    case "checking":
      return (
        <Text size="xs" c="dimmed">
          Checking availability…
        </Text>
      );
    case "available":
      return (
        <Text size="xs" c="green" className="inline-flex items-center gap-1">
          <IconCheck size={12} />
          Available
        </Text>
      );
    case "taken":
      return (
        <Text size="xs" c="red" className="inline-flex items-center gap-1">
          <IconX size={12} />
          Already taken
        </Text>
      );
  }
}
